import classNames from 'classnames';
import React, { FC, useContext } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useGenerateSliderOptions } from './hooks';
import RadioSliderField from 'components/RadioSliderField';
import RadioGroupField from 'components/RadioGroupField';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';
import {
  CommercialProperty,
  OwnerOfTheBusiness,
  RevenueModel,
  SaleType,
  Succession,
} from 'models/Listing';
import { YesNo } from 'models/Fields';
import { REQUIRED_VISIBLE_INFO } from 'data/global';
import ListingFieldRequiredVisibleInfo from '../ListingFieldRequiredVisibleInfo';
import useListingFieldIsRequired from '../../hooks/useListingFieldIsRequired';
import { getFieldValidations } from 'validations/general';

import './OwnerOfBusiness.styles.scss';

const OwnerOfBusiness: FC = () => {
  const { t } = useTranslation();

  const { patchListingValue } = useContext(PatchListingContext);

  const {
    values: {
      hasCommercialProperty,
      saleType,
      contractedCustomers,
      isEnterprise,
    },
  } = useFormState();

  const {
    saleTypeOptions,
    revenueModelOptions,
    ownerOfBusinessOptions,
    replaceOwnerBusinessOptions,
    commercialPropertyOptions,
    successionOptions,
    contractedCustomersOptions,
  } = useGenerateSliderOptions();

  const [isSuccessionRequired, isSuccessionDefaultRequired] =
    useListingFieldIsRequired('succession');
  const [
    isHasCommercialPropertyRequired,
    isHasCommercialPropertyDefaultRequired,
  ] = useListingFieldIsRequired('hasCommercialProperty');
  const [isRevenueModelRequired, isRevenueModelDefaultRequired] =
    useListingFieldIsRequired('revenueModel');

  const radioSliderClass = classNames({
    'slider-enterprise': isEnterprise,
  });

  return (
    <div className={classNames('white-panel', 'lcw-owner-of-business')}>
      {isEnterprise ? (
        <>
          <div className="lcw-owner-of-business__title-with-info">
            <p className="statement-of-information__title">{t('succession')}</p>
            <ListingFieldRequiredVisibleInfo
              fieldName={`succession${REQUIRED_VISIBLE_INFO}`}
              fieldLabel={t('succession')}
              isDefaultRequired={isSuccessionDefaultRequired}
              canChangeRequired={false}
            />
          </div>
          <Field
            name="succession"
            slideSteps={successionOptions}
            component={RadioSliderField}
            customOnChange={(succession: Succession) =>
              patchListingValue({ succession })
            }
            {...getFieldValidations(isSuccessionRequired)}
          />
        </>
      ) : (
        <>
          <p className="statement-of-information__title">
            {t('theOwnersBusiness')}
          </p>
          <RadioGroupField
            name="ownerOfTheBusiness"
            options={ownerOfBusinessOptions}
            customOnChange={(ownerOfTheBusiness: OwnerOfTheBusiness) => {
              patchListingValue({ ownerOfTheBusiness });
            }}
          />
          <p className="statement-of-information__title">
            {t('someoneInTheBusinessToReplace')}
          </p>
          <RadioGroupField
            name="someoneToReplaceOwner"
            options={replaceOwnerBusinessOptions}
            customOnChange={(someoneToReplaceOwner: YesNo) => {
              patchListingValue({
                someoneToReplaceOwner: someoneToReplaceOwner === YesNo.Yes,
              });
            }}
          />
        </>
      )}

      <p className="statement-of-information__title">
        {t('commercialProperty')}
      </p>
      <div className="lcw-owner-of-business__title-with-info">
        <Field
          name="hasCommercialProperty"
          type="checkbox"
          label={t('businessOwnerOwnCommercialProperty')}
          component={CheckboxField}
          customOnChange={(hasCommercialProperty: boolean) =>
            patchListingValue({
              hasCommercialProperty,
            })
          }
          {...getFieldValidations(isHasCommercialPropertyRequired)}
        />
        <ListingFieldRequiredVisibleInfo
          fieldName={`hasCommercialProperty${REQUIRED_VISIBLE_INFO}`}
          fieldLabel={t('businessOwnerOwnCommercialProperty')}
          isDefaultRequired={isHasCommercialPropertyDefaultRequired}
          canChangeRequired={false}
        />
      </div>

      {hasCommercialProperty && (
        <RadioGroupField
          name="commercialProperty"
          className="lcw-owner-of-business__sub-option"
          options={commercialPropertyOptions}
          customOnChange={(commercialProperty: CommercialProperty) => {
            patchListingValue({
              commercialProperty,
            });
          }}
        />
      )}

      <p className="statement-of-information__title">
        {t('additionalDetails')}
      </p>
      <Field
        name="saleType"
        component={RadioSliderField}
        className={radioSliderClass}
        slideSteps={saleTypeOptions}
        {...(!saleType && { defaultIndex: -1 })}
        label={
          <>
            {t('saleType')} {isEnterprise && t('(optional)')}
          </>
        }
        customOnChange={(saleType: SaleType) =>
          patchListingValue({
            saleType,
          })
        }
      />
      <Field
        name="contractedCustomers"
        component={RadioSliderField}
        slideSteps={contractedCustomersOptions}
        className={radioSliderClass}
        {...(!contractedCustomers && { defaultIndex: -1 })}
        label={
          <>
            {t('contractedCustomers')} {isEnterprise && t('(optional)')}
          </>
        }
        customOnChange={(contractedCustomers: YesNo) =>
          patchListingValue({
            contractedCustomers: contractedCustomers === YesNo.Yes,
          })
        }
      />
      <Field
        className="revenue-model-field"
        name="revenueModel"
        label={t('revenueModel')}
        labelChildren={
          <ListingFieldRequiredVisibleInfo
            fieldName={`revenueModel${REQUIRED_VISIBLE_INFO}`}
            fieldLabel={t('revenueModel')}
            isDefaultRequired={isRevenueModelDefaultRequired}
            canChangeRequired={false}
          />
        }
        slideSteps={revenueModelOptions}
        component={RadioSliderField}
        customOnChange={(revenueModel: RevenueModel) =>
          patchListingValue({ revenueModel })
        }
        {...getFieldValidations(isRevenueModelRequired)}
      />
    </div>
  );
};

export default OwnerOfBusiness;
